<template>
  <UIPopup
    v-model="modelValue"
    :closeable="false"
    :max-width="isDesktop ? '900px' : isTablet ? '553px' : ''"
    @close="modelValue = false"
  >
    <template #header>
      <h3 class="mt-10 text-2xl text-black-80">{{ $t('onboarding.verifyPhone.title') }}</h3>
    </template>
    <template #body>
      <div class="mx-auto flex max-w-[486px] flex-col gap-4">
        <div class="flex flex-col gap-2">
          <div class="flex flex-row justify-center gap-2">
            <span>{{ $t('onboarding.verifyPhone.sentCode') }}</span>
            <span class="font-bold">{{ hiddenPhone }}</span>
          </div>
          <div class="flex flex-row items-center justify-center gap-2">
            <span class="text-caption-1">{{ $t('onboarding.verifyPhone.wrongNumber') }}</span>
            <UIButton id="update" variant="ghost" class="!text-caption-1" @click="showContactUpdatePopup">{{
              $t('onboarding.verifyPhone.updateNumber')
            }}</UIButton>
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex flex-row justify-center gap-4">
            <input
              v-for="(__, index) in code"
              :key="index"
              :ref="(el) => (refs[`code_${index}`].value = el as HTMLInputElement)"
              v-model="code[index]"
              type="number"
              class="h-[64px] w-1/6 rounded-lg border-2 border-black-20 text-center text-[20px] leading-[84px] [appearance:textfield] tablet:h-[128px] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              placeholder="*"
              maxlength="1"
              @input="focusOnNextInput($event, index)"
              @keydown="goBackOnBackspace($event, index)"
            />
          </div>
          <div v-if="isCodeInvalid" class="my-4 rounded-xl bg-additional-salmon-20 p-4 text-start">
            <h5 class="text-subhead-4 text-additional-bordo-100">Verification code is incorrect</h5>
          </div>
          <div class="flex flex-row items-center justify-center gap-2">
            <span class="text-caption-1">Didn’t get a code?</span>
            <UIButton id="resend" variant="ghost" class="!text-caption-1" :disabled="resendTimer > 0" @click="resend"
              >Click to resend {{ resendTimer > 0 ? `in ${resendTimer}s` : '' }}
            </UIButton>
          </div>
        </div>
        <p class="text-body-2">
          It is important to verify as you will receive important information about your appointment.
        </p>
      </div>
    </template>
    <template #buttons>
      <UIButton
        id="verify"
        class="mx-auto w-full max-w-[488px]"
        :disabled="isCodeInvalid || code.some((char) => char.length !== 1)"
        :loading="isPending"
        @click="verify"
        >Verify
      </UIButton>
    </template>
  </UIPopup>
</template>

<script setup lang="ts">
import { POPUPS } from '../dynamic/maps'

const modelValue = defineModel<boolean>()
const authStore = useAuthStore()

const { isTablet, isDesktop } = useAppBreakpoints()

type Props = {
  force?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  force: false
})

const refs = {
  code_0: ref<HTMLInputElement | null>(null),
  code_1: ref<HTMLInputElement | null>(null),
  code_2: ref<HTMLInputElement | null>(null),
  code_3: ref<HTMLInputElement | null>(null),
  code_4: ref<HTMLInputElement | null>(null),
  code_5: ref<HTMLInputElement | null>(null),
}
const code = ref(['', '', '', '', '', ''])
const isCodeInvalid = ref(false)

const hiddenPhone = computed(() => {
  const phone = authStore.getUser?.phone
  if (!phone) return ''
  const firstPart = phone.substring(0, 4)
  const lastPart = phone.substring(phone.length - 3)
  const middlePart = '*'.repeat(Math.max(0, phone.length - 7))
  return firstPart + middlePart + lastPart
})

const { mutateAsync: sendCode } = useSendVerifyCode()

const resendTimer = ref()

onNuxtReady(async () => {
  await sendCode(props.force)
  // Focus the first input field with null check
  if (refs.code_0.value) {
    refs.code_0.value.focus()
  }
})

const focusOnNextInput = (event: Event, index: number) => {
  isCodeInvalid.value = false
  code.value[index] = (event.target as HTMLInputElement).value
  // ensure only the latest 1 char
  code.value[index] = code.value[index].slice(-1)

  // focus on next input
  const target = event.target as HTMLInputElement
  if (target.value.length === 1 && index < 5) {
    // Type-safe way to access the refs
    const nextInputKey = `code_${index + 1}` as keyof typeof refs
    const nextInput = refs[nextInputKey].value
    if (nextInput) {
      nextInput.focus()
    }
  }

  // verify if all inputs are filled
  if (code.value.every((char) => char.length === 1)) {
    verify()
  }
}

const goBackOnBackspace = (event: KeyboardEvent, index: number) => {
  // ensure char is removed
  code.value[index] = ''
  // go back to previous input
  if (event.key === 'Backspace' && index > 0) {
    // Type-safe way to access the refs
    const prevInputKey = `code_${index - 1}` as keyof typeof refs
    const prevInput = refs[prevInputKey].value
    if (prevInput) {
      prevInput.focus()
    }
  }
}

const { mutateAsync: verifyPhone, isPending } = useVerifyPhone()

const verify = () => {
  const codeString = code.value.join('')
  verifyPhone(codeString)
    .then(() => {
      const user = useAuthStore()?.getUser
      if (!user?.answeredQuestions) {
        useUIStore().showPopup(POPUPS.FILL_PROFILE)
      } else if (!user?.bookedFirstCall) {
        useUIStore().showPopup(POPUPS.BOOK_A_CALL)
      } else {
        modelValue.value = false
      }
    })
    .catch(() => {
      isCodeInvalid.value = true
    })
}

const resend = () => {
  sendCode(true)
  code.value = ['', '', '', '', '', '']
  // Focus with null check
  if (refs.code_0.value) {
    refs.code_0.value.focus()
  }
  resendTimer.value = 30
  const interval = setInterval(() => {
    resendTimer.value--
    if (resendTimer.value === 0) {
      clearInterval(interval)
    }
  }, 1000)
}

const showContactUpdatePopup = () => {
  useUIStore().showPopup(POPUPS.UPDATE_PHONE)
}
</script>

<style scoped lang="scss"></style>
